/* eslint-disable max-len */
import identity from 'ramda/src/identity'
import React, { memo, useRef } from 'react'
import classNames from 'classnames'
import withStyles from '@material-ui/core/styles/withStyles'
import Typography from '@material-ui/core/Typography'
import NoSsr from '@material-ui/core/NoSsr'
import TextField from '@material-ui/core/TextField'
import Paper from '@material-ui/core/Paper'
import Chip from '@material-ui/core/Chip'
import Avatar from '@material-ui/core/Avatar'
import MenuItem from '@material-ui/core/MenuItem'
import CancelIcon from '@material-ui/icons/Cancel'
import ListItemAvatar from '@material-ui/core/ListItemAvatar'
import ListItemText from '@material-ui/core/ListItemText'
import Tappable from 'react-tappable/lib/Tappable'
import AsyncSelect from 'root/src/client/web/base/AsyncSelect'

import autoCompleteSetInputHandler from 'root/src/client/logic/form/handlers/autoCompleteSetInputHandler'
import autoCompleteConnector from 'root/src/client/logic/form/connectors/autoCompleteConnector'
import { orNull } from 'root/src/shared/util/ramdaPlus'
import loadOptionsPromise from 'root/src/client/logic/form/util/loadOptionsPromise'
import { colorsNew } from 'root/src/client/web/commonStyles'
import InputStrengthComponent from 'root/src/client/web/form/InputStrength'

const controlStyles = {
	input: {
		display: 'flex',
		padding: [[10, 14]],
	},
}
const inputComponent = memo(({ inputRef, ...props }) => (
	<div ref={inputRef} {...props} />
))
const Control = withStyles(controlStyles, { name: 'MuiControl' })(
	memo(({ classes, children, innerRef, innerProps, selectProps }) => (
		<TextField
			fullWidth
			InputProps={{
				inputComponent,
				inputProps: {
					className: classes.input,
					inputRef: innerRef,
					children,
				},
				...innerProps,
			}}
			variant="outlined"
			{...selectProps.textFieldProps}
		/>
	)),
)

const optionStyles = {
	listItemText: {
		// color: 'white',
	},
	option: {

		fontWeight: 400,
		backgroundColor: colorsNew.dark2,
		'&:hover': {
			backgroundColor: colorsNew.dark1,
		},
		'& span': {
			color: 'white',
		},
	},
	selected: {
		// color: 'white',
		fontWeight: 500,
		// backgroundColor: primaryColor,
	},
	active: {
		backgroundColor: colorsNew.dark1,
	},
}
const Option = withStyles(optionStyles, { name: 'MuiOption' })(
	memo(({
		classes, children, innerRef, isFocused, isSelected, innerProps,
		isActive,
	}) => {
		const { image, label } = children

		return (
			<MenuItem
				buttonRef={innerRef}
				selected={isFocused}
				component="div"
				className={classNames(
					isSelected ? classes.selected : classes.option,
					isActive && classes.active,
				)}
				{...innerProps}
			>
				{orNull(
					image,
					<ListItemAvatar>
						<Avatar alt={label} src={image} />
					</ListItemAvatar>,
				)}
				<ListItemText className={classes.listItemText} primary={label} />
			</MenuItem>
		)
	}),
)

const placeholderStyles = {
	placeholder: {
		position: 'absolute',
		left: 14,
		fontSize: 16,
	},
}
const Placeholder = withStyles(placeholderStyles, { name: 'MuiPlaceholder' })(
	memo(({ classes, children, innerProps }) => (
		<Typography
			color="textSecondary"
			className={classes.placeholder}
			{...innerProps}
		>
			{children}
		</Typography>
	)),
)

const valueContainerStyles = {
	valueContainer: {
		display: 'flex',
		flexWrap: 'wrap',
		flexDirection: 'row',
		overflow: 'hidden',
		alignItems: 'center',
		width: '100%',
		padding: 0,
	},
}
const ValueContainer = withStyles(valueContainerStyles, { name: 'MuiValueContainer' })(
	memo(({ classes, children, locked }) => (
		<div
			className={classNames(
				classes.valueContainer,
			)}
		>
			{children}
			{locked}
		</div>
	)),
)

const chipStyles = {
	root: {
		height: 34,
		margin: 5,
		backgroundColor: colorsNew.dark1,
	},
}
const MultiValue = withStyles(chipStyles, { name: 'MuiChip' })(
	memo(({ children, classes, innerProps, removeProps, locked }) => {
		const { image, label } = children

		return (
			<Chip
				avatar={orNull(
					image,
					<Avatar alt={label} src={image} />,
				)}
				tabIndex={-1}
				classes={classes}
				style={locked ? { cursor: 'not-allowed' } : {}}
				label={label}
				onDelete={locked ? undefined : () => removeProps.onClick(children)}
				deleteIcon={locked ? <div /> : (
					<Tappable onTap={() => removeProps.onClick(children)}>
						<CancelIcon />
					</Tappable>
				)}
				{...innerProps}
			/>
		)
	}),
)

const menuStyles = {
	paper: {
		position: 'absolute',
		zIndex: 100,
		left: 0,
		right: 0,
		overflow: 'auto',
		maxHeight: '175px',
	},
}
const Menu = withStyles(menuStyles, { name: 'MuiMenu' })(
	memo(({ classes, children, innerProps }) => (
		<Paper
			square
			className={classes.paper}
			{...innerProps}
		>
			{children}
		</Paper>
	)),
)

export const autoCompleteComponents = {
	Control,
	Menu,
	MultiValue,
	Option,
	Placeholder,
	ValueContainer,
}

export const AutoCompleteUnconnected = memo(({
	moduleKey, fieldPath, setInput, value, fieldLabel,
	fieldError, fieldHasError, arrayFieldMaxItems,
	fieldPlaceholder, isMulti, optionsPromiseType, locked, moduleId, fieldId,
	debounceDelay, inputHeight, onExit, inputStrength, fieldActionButton,
	fieldActionButtonFn, fieldIcon, fieldTheme, loadOptions,
}) => {
	const elementRef = useRef()
	return (
		<NoSsr>
			<AsyncSelect
				ref={elementRef}
				loadOptions={loadOptions ?? loadOptionsPromise(optionsPromiseType)}
				debounceDelay={debounceDelay}
				textFieldProps={{
					label: fieldLabel,
					error: fieldHasError,
					helperText: fieldError,
					InputLabelProps: {
						shrink: true,
					},
				}}
				fieldTheme={fieldTheme}
				placeholder={fieldPlaceholder}
				components={autoCompleteComponents}
				value={value}
				onSelect={autoCompleteSetInputHandler(
					moduleKey, fieldPath, arrayFieldMaxItems, setInput,
					isMulti, onExit, elementRef.current,
				)}
				isMulti={isMulti}
				locked={locked}
				isFormStyle
				fieldIcon={fieldIcon}
				cacheOptions
				getOptionLabel={identity}
				getValue={identity}
				inputHeight={inputHeight}
				fieldActionButton={fieldActionButton}
				fieldActionButtonFn={fieldActionButtonFn}
				fieldId={fieldId}
				moduleKey={moduleKey}
				moduleId={moduleId}
			/>
			{inputStrength && <InputStrengthComponent maxLength={arrayFieldMaxItems} length={value.length} strengthDescription={inputStrength} />}
		</NoSsr>
	)
})

export default autoCompleteConnector(AutoCompleteUnconnected)
